import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useLoadingState } from '@common/hooks';
import { useTranslations } from '@lang';
import moment from 'moment';
import { FC, useCallback } from 'react';
import { useRouter } from 'shared-routing';

import type { ListItemCardProps } from 'voyager';
import { SpaCategoryDetailTemplate } from 'voyager';

import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useGetAllSpaPackages } from '@features/spa/hooks/useGetAllSpaPackages';
import { useGetAvailableSpaPackages } from '@features/spa/hooks/useGetAvailableSpaPackages';
import { useGetSpaDaysPage } from '@features/spa/hooks/useGetSpaDaysPage';
import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';
import { SpaPackageListingType } from 'shared-types/SpaPackage';

import type { SpaDaysCategoryV2Props } from './SpaDaysCategoryV2.types';

export const SpaDaysCategoryV2: FC<SpaDaysCategoryV2Props> = ({
  property,
  journeyParams,
  language,
  category,
}) => {
  const router = useRouter();
  const { t } = useTranslations();

  const isUncategorized = String(category.id) === UNCATEGORIZED_VALUE;
  const selectedDateString = moment(journeyParams.spaDate).format('YYYY-MM-DD');

  const { data: spaDaysAll = [], isFetching: isFetchingAll } = useGetAllSpaPackages(
    property.id,
    language
  );
  const { data: spaDaysAvailable = [], isFetching: isFetchingAvailable } =
    useGetAvailableSpaPackages(
      property.id,
      { category: isUncategorized ? null : Number(category.id), date: selectedDateString },
      language
    );
  const { data: spaDaysPage } = useGetSpaDaysPage(property.id, language);
  const fallbackImageUrl = spaDaysPage?.heroBackgroundImage?.url;

  const isFetching = isFetchingAll || isFetchingAvailable;
  const { isInitialLoading, isRefetching } = useLoadingState(isFetching, spaDaysAll.length > 0);

  const categoryName = isUncategorized ? t('spa.noCategory') : category.name;

  const categoryPackages = isUncategorized
    ? spaDaysAll.filter((sp: SpaPackageListingType) => !sp.categories || sp.categories.length === 0)
    : spaDaysAll.filter((sp: SpaPackageListingType) =>
        sp.categories?.some((cat) => String(cat.id) === String(category.id))
      );

  const items: ListItemCardProps[] = categoryPackages
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((sp) => {
      const isAvailable = spaDaysAvailable.some((avail) => avail.id === sp.id);
      return {
        id: String(sp.id),
        name: sp.name,
        image: {
          src: sp.image?.url || fallbackImageUrl,
          alt: sp.name,
        },
        price:
          sp.price?.amount != null
            ? {
                amount: sp.price.amount,
                currencyCode: sp.price.currencyCode,
                label: t('common.from'),
              }
            : undefined,
        unavailable: !isFetchingAvailable && !isAvailable,
      };
    });

  const handleSelectItem = useCallback(
    (id: string) => {
      router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id } });
    },
    [router]
  );

  const handleEditSearch = useCallback(() => {
    router.push({
      pathname: Routes.SPA_DAY_LIST,
      query: { arrivalDate: selectedDateString },
    });
  }, [router, selectedDateString]);

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const journeyBar = (
    <JourneyPickerV2Connected
      imageOverlay
      dateMode="single"
      dateContext="spa"
      showGuests
      numberOfMonths={1}
      categories={navCategories}
      category="spa-days"
      onCategoryChange={handleNavCategoryChange}
    />
  );

  return (
    <>
      <Spinner showDelayMessage isActive={isRefetching} />
      <SpaCategoryDetailTemplate
        categoryName={categoryName}
        categoryDescription={isUncategorized ? undefined : category.description}
        categoryImageUrl={
          isUncategorized ? fallbackImageUrl : category.image?.url || fallbackImageUrl
        }
        items={items}
        isLoading={isInitialLoading}
        onSelectItem={handleSelectItem}
        onEditSearch={handleEditSearch}
        journeyBar={journeyBar}
        labels={{
          buttonLabel: t('common.moreDetails'),
          unavailableLabel: t('common.unavailableForSearch'),
          editSearchLabel: t('common.findOtherDates'),
        }}
      />
    </>
  );
};
